import { useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { FetchYearlyCashbackDetails } from 'Services';
import { IAPI_ACTION, IDispatcherType, IInitialState, INITIAL_STATE } from 'types';
import Toasty from 'components/atoms/Toasty';

export interface IYearlyCashbackApiData {
	is_cashback_given: boolean;
	device_serial: string | null;
	cashback_id: number | null;
	cashback_amount: number | null;
	cashback_date: string | null;
	terminal_activation_date: string | null;
	terminal_order_date: null;
	payment_utr: string | null;
}

interface IResponse extends Omit<IInitialState, 'data'> {
	data: IYearlyCashbackApiData;
}

export const setAPIError = (state: ReturnType<typeof INITIAL_STATE>, action: IAPI_ACTION): void => {
	state.loading = false;
	state.error = action.error.message || 'Something went wrong';
	Toasty.error(state.error);
};

export const fetchYearlyCashbackData = createAsyncThunk('get/yearlyChashbackDetails', FetchYearlyCashbackDetails);

export const YearlyCashbackSlice = createSlice({
	name: 'yearlyCashbackDetails',
	initialState: INITIAL_STATE(),
	reducers: {
		RESET: (state: ReturnType<typeof INITIAL_STATE>) => Object.assign(state, INITIAL_STATE()),
	},
	extraReducers: {
		[fetchYearlyCashbackData.pending.toString()]: (state: ReturnType<typeof INITIAL_STATE>) => {
			state.data = {};
			state.error = '';
			state.loading = true;
			state.isFetched = false;
		},
		[fetchYearlyCashbackData.rejected.toString()]: setAPIError,
		[fetchYearlyCashbackData.fulfilled.toString()]: (
			state: ReturnType<typeof INITIAL_STATE>,
			action: IAPI_ACTION<any>,
		) => {
			const { payload } = action;

			state.loading = false;
			state.isFetched = true;
			state.error = '';
			state.data = payload;
		},
	},
});

interface IStates {
	[YearlyCashbackSlice.name]: ReturnType<typeof YearlyCashbackSlice.reducer>;
}

export const useYearlyCashbackDetails = (): IResponse =>
	useSelector((state: IStates) => state[YearlyCashbackSlice.name] || {}) as IResponse;

export const triggerYearlyCashbackApi =
	() =>
	async (dispatch: IDispatcherType): Promise<void> => {
		await dispatch(fetchYearlyCashbackData());
		return;
	};

export const resetYearlyCashbackDetails =
	() =>
	async (dispatch: IDispatcherType): Promise<void> => {
		await dispatch(YearlyCashbackSlice.actions.RESET());
		return;
	};
